import React from 'react';
import SectionCard from './SectionCard';
import ReadonlyField from './ReadonlyField';
import { Loader2 } from 'lucide-react';

const MergeFieldsSection = ({ submission, mergeFieldsConfig, loadingMergeFields, isDark }) => {
    if (loadingMergeFields && submission?.mergeFieldsData && typeof submission.mergeFieldsData === 'object' && Object.keys(submission.mergeFieldsData).length > 0) {
        return (
            <SectionCard isDark={isDark} className="items-center justify-center min-h-[100px]">
                <Loader2 className={`w-[24px] h-[24px] animate-spin transition-colors duration-200 ${
                    isDark ? 'text-[#b3b3b3]' : 'text-[#072837]'
                }`} />
                <div className={`text-[14px] font-normal transition-colors duration-200 ${
                    isDark ? 'text-[#b3b3b3]' : 'text-[#898989]'
                }`}>
                    Loading merge fields...
                </div>
            </SectionCard>
        );
    }

    if (!submission?.mergeFieldsData || typeof submission.mergeFieldsData !== 'object' || Object.keys(submission.mergeFieldsData).length === 0) {
        return null;
    }

    // Group fields by their label
    const groupedByLabel = {};
    Object.entries(submission.mergeFieldsData).forEach(([fieldName, fieldValue]) => {
        // Skip if no value
        if (!fieldValue) return;

        const fieldConfig = mergeFieldsConfig.find(f => f.field_name === fieldName);
        const fieldLabel = fieldConfig?.field_label || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        if (!groupedByLabel[fieldLabel]) {
            groupedByLabel[fieldLabel] = [];
        }

        groupedByLabel[fieldLabel].push({
            fieldName,
            fieldValue,
            fieldConfig,
            fieldLabel
        });
    });

    return Object.entries(groupedByLabel).map(([label, fields]) => {
        // If only one field with this label, render normally
        if (fields.length === 1) {
            const { fieldName, fieldValue, fieldConfig, fieldLabel } = fields[0];
            const fieldType = fieldConfig?.field_type || 'free_text';

            // Format datetime fields based on type from config
            let displayValue = fieldValue;
            if (fieldType === 'date_time' && typeof fieldValue === 'string' && fieldValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
                try {
                    const [datePart, timePart] = fieldValue.split('T');
                    const [year, month, day] = datePart.split('-');
                    const [hours, minutes] = timePart.split(':');
                    const localDate = new Date(year, month - 1, day, hours, minutes);

                    displayValue = new Intl.DateTimeFormat('en-GB', {
                        day: 'numeric',
                        month: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    }).format(localDate);
                } catch (e) {
                    displayValue = fieldValue;
                }
            }

            return (
                <SectionCard key={fieldName} isDark={isDark}>
                    <ReadonlyField label={fieldLabel} value={displayValue} isDark={isDark} />
                </SectionCard>
            );
        }

        // Multiple fields with same label - render in grid
        return (
            <SectionCard key={label} isDark={isDark}>
                <p className={`m-0 text-[16px] font-semibold transition-colors duration-200 ${
                    isDark ? 'text-white' : 'text-[#072837]'
                }`}>
                    {label}
                </p>
                <div className="grid grid-cols-2 gap-[16px] w-full">
                    {fields.map(({ fieldName, fieldValue, fieldConfig }) => {
                        const fieldType = fieldConfig?.field_type || 'free_text';

                        // Format datetime fields based on type from config
                        let displayValue = fieldValue;
                        if (fieldType === 'date_time' && typeof fieldValue === 'string' && fieldValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
                            try {
                                const [datePart, timePart] = fieldValue.split('T');
                                const [year, month, day] = datePart.split('-');
                                const [hours, minutes] = timePart.split(':');
                                const localDate = new Date(year, month - 1, day, hours, minutes);

                                displayValue = new Intl.DateTimeFormat('en-GB', {
                                    day: 'numeric',
                                    month: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                }).format(localDate);
                            } catch (e) {
                                displayValue = fieldValue;
                            }
                        }

                        return (
                            <ReadonlyField key={fieldName} value={displayValue} isDark={isDark} />
                        );
                    })}
                </div>
            </SectionCard>
        );
    });
};

export default MergeFieldsSection;
